import {
  ancestryTreeToHTML,
  buildComponentAncestryTree,
  constructComponent,
} from './componentGenerators/renderer'
import Attrs from './models/Attrs'
import type { Component, Flow, FlowPage, GlobalComponentLocation, Transition } from './models/types'
import { getTopFlowClasses } from './styles/formatSelector'
import { generatePageSelectors, generateElementSelectors } from './styles/selectors'
import {
  POPUP_CONTROLLER,
  FLOW_CONTROLLER,
  PAGE_CONTROLLER,
  FLOW_EDIT_CONTROLLER,
  JSON_UPDATE_CONTROLLER,
  CONDITIONAL_TAGS_CONTROLLER,
} from './helpers/controllers'
import { STIMULUS_ATTRIBUTES } from './componentGenerators/helpers'
import { CONTROLLERS } from './helpers/controllers'
import { getBaseBuilderVersion } from './helpers/utils'
import { LoggerWithChild } from './helpers/logger/types'

function generatePageHtml(
  page: FlowPage,
  flow: Flow,
  groupId: string,
  projectId: string,
  components: Component[] | null,
  editingMode: boolean,
  liteEditingMode: boolean,
  allowJsonUpdate: boolean,
  static_render: boolean,
  userData: { [key: string]: unknown } = {},
  logger?: LoggerWithChild
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const pageComponents = ancestryTreeToHTML(
    buildComponentAncestryTree(components || page.components || [], builderVersion),
    page,
    flow,
    groupId,
    projectId,
    null,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    logger
  )

  if (components) {
    return pageComponents
  }

  const pageTopGlobalComponents = globalsAtLocation(
    page,
    'page_top',
    flow,
    groupId,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    projectId
  )
  const beforeComponentsGlobalComponents = globalsAtLocation(
    page,
    'before_components',
    flow,
    groupId,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    projectId
  )
  const afterComponentsGlobalComponents = globalsAtLocation(
    page,
    'after_components',
    flow,
    groupId,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    projectId
  )
  /* Unneeded? Footer is a v < 3 thing */
  // const footerGlobalComponents = globalsAtLocation('footer')
  const pageBottomGlobalComponents = globalsAtLocation(
    page,
    'page_bottom',
    flow,
    groupId,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    projectId
  )

  const pageContentsAttrs = new Attrs(static_render).addDOMClass(
    'form-page-main',
    generateElementSelectors({
      elementType: 'PageContents',
      page,
      builderVersion,
    }).join(' ')
  )

  //* I add into page-contents the properties for page transition to be able to pass them into page when replacing html if necessary
  const { transition: enterTransition, usePageClass: usePageEnterClass } = definePageTransition(
    page,
    flow,
    'enter'
  )
  if (usePageEnterClass)
    addPageTransitionAttr(page, flow, pageContentsAttrs, 'enter', enterTransition, true)

  const { transition: exitTransition, usePageClass: usePageExitClass } = definePageTransition(
    page,
    flow,
    'exit'
  )
  if (usePageExitClass)
    addPageTransitionAttr(page, flow, pageContentsAttrs, 'exit', exitTransition, true)

  return `${pageTopGlobalComponents}
    <div ${pageContentsAttrs}>
      ${beforeComponentsGlobalComponents}
      ${pageComponents}
      ${afterComponentsGlobalComponents}
    </div>
    ${pageBottomGlobalComponents}`
}

export function generateBodyHtml(
  context: {
    origin: string
    flowId: string
    groupId: string
    projectId: string
    version: number
    css: string
    userData: { [key: string]: unknown }
    popup: boolean
    editingMode: boolean
    liteEditingMode: boolean
    allowJsonUpdate: boolean
    static_render: boolean
    repeatables?: Component[]
    globalLocation?: string | null
    branchId?: string | null
    logger?: LoggerWithChild
  },
  pageContentOnly: boolean,
  componentContentOnly: boolean,
  flow: Flow,
  page: FlowPage,
  components: Component[] | null,
  component?: Component,
  stripePublishableKey?: string
): string {
  const {
    groupId,
    projectId,
    globalLocation,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    logger,
  } = context
  if (context.repeatables) {
    return JSON.stringify(
      context.repeatables.map((comp) =>
        renderSingleComponent(
          comp,
          page,
          flow,
          groupId,
          projectId,
          globalLocation || null,
          editingMode,
          liteEditingMode,
          allowJsonUpdate,
          static_render,
          userData,
        )
      )
    )
  }

  if (componentContentOnly && component) {
    return renderSingleComponent(
      component,
      page,
      flow,
      groupId,
      projectId,
      globalLocation || null,
      editingMode,
      liteEditingMode,
      allowJsonUpdate,
      static_render,
      userData
    )
  }
  const pageHtml = generatePageHtml(
    page,
    flow,
    groupId,
    projectId,
    components,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    logger
  )
  if (pageContentOnly) return pageHtml

  const userDataParamString =
    typeof context.userData === 'object'
      ? Object.entries(context.userData)
          .map(([key, value]) => `${key}=${value}`)
          .join('&')
      : ''

  const beforePageGlobalComponents = globalsAtLocation(
    page,
    'before_page',
    flow,
    groupId,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData,
    context.projectId
  )

  const afterPageGlobalComponents =
    globalsAtLocation(
      page,
      'after_page',
      flow,
      groupId,
      editingMode,
      liteEditingMode,
      allowJsonUpdate,
      static_render,
      userData,
      context.projectId
    ) +
    globalsAtLocation(
      page,
      'flow_bottom',
      flow,
      groupId,
      editingMode,
      liteEditingMode,
      allowJsonUpdate,
      static_render,
      userData,
      context.projectId
    ) +
    globalsAtLocation(
      page,
      'footer',
      flow,
      groupId,
      editingMode,
      liteEditingMode,
      allowJsonUpdate,
      static_render,
      userData,
      context.projectId
    ) // 'footer' will be deprecated soon but leaving backwards compatible for now

  const pageSelectors = generatePageSelectors(page).join(' ')
  const startingFlowClasses = getTopFlowClasses(context.flowId)

  const flowAttrs = new Attrs(context.static_render)
    .addController(FLOW_CONTROLLER)
    .addAction(
      `${PAGE_CONTROLLER}:next->${FLOW_CONTROLLER}#next`,
      `${PAGE_CONTROLLER}:update->${FLOW_CONTROLLER}#update`
    )
    .addValue(FLOW_CONTROLLER, 'url', `${context.origin}/${context.flowId}`)
    .addValue(FLOW_CONTROLLER, 'id', context.flowId)
    .addValue(FLOW_CONTROLLER, 'group-id', context.groupId)
    .addValue(FLOW_CONTROLLER, 'project-id', context.projectId)
    .addValue(FLOW_CONTROLLER, 'page-id', page.id)
    .addValue(FLOW_CONTROLLER, 'version', `${context.version}`)
    .addValue(FLOW_CONTROLLER, 'branch-id', `${context.branchId}`)
    .addValueIfExists(
      FLOW_CONTROLLER,
      STIMULUS_ATTRIBUTES[CONTROLLERS.flow].disableAllInteractionsDuringTransitions,
      flow.disable_all_interactions_during_transitions
    )
    .addValueIfExists(FLOW_CONTROLLER, 'stripe-publishable-key', stripePublishableKey)
    .addDOMClass(startingFlowClasses)
    .addClass(FLOW_CONTROLLER, 'default', startingFlowClasses.join(' '))

  // .add(
  //   'data-snippyly-location',
  //   JSON.stringify({
  //     flow_id: context.flowId,
  //     page_id: page.id,
  //   })
  // ) //* I'm commenting this out since it was not being set correctly and it is not in used for the moment

  if (editingMode)
    flowAttrs
      .addController(FLOW_EDIT_CONTROLLER)
      .addValue(FLOW_EDIT_CONTROLLER, 'is-editing-flow', 'false')
  if (editingMode || liteEditingMode || allowJsonUpdate)
    flowAttrs
      .addController(JSON_UPDATE_CONTROLLER)
      .addValue(JSON_UPDATE_CONTROLLER, 'type', `styles`)
      .addValue(JSON_UPDATE_CONTROLLER, 'path', `styles`)
      .addValue(JSON_UPDATE_CONTROLLER, 'flow-id', context.flowId)
      .addValue(JSON_UPDATE_CONTROLLER, 'watch-global-components', 'true')
      .addClass(JSON_UPDATE_CONTROLLER, 'loading', 'loading')

  const pageAttrs = new Attrs(context.static_render)
    .addController(PAGE_CONTROLLER)
    .addController(CONDITIONAL_TAGS_CONTROLLER)
    .addDOMClass([`FormPage`, pageSelectors])
    .addAction(
      `submit->${PAGE_CONTROLLER}#cancelSubmitEvent`,
      `${PAGE_CONTROLLER}:info-box->${FLOW_CONTROLLER}#setInfoBoxPageId`,
      `${PAGE_CONTROLLER}:scroll-to-top->${FLOW_CONTROLLER}#scrollToTop`
    )
    .addValue(PAGE_CONTROLLER, `url`, `${context.origin}/${context.flowId}`)
    .addValue(PAGE_CONTROLLER, `page-id`, page.id)
    .addValue(PAGE_CONTROLLER, `html-page-id`, page.id)
    .addValue(CONDITIONAL_TAGS_CONTROLLER, `page-id`, page.id)
    .addValue(PAGE_CONTROLLER, `page-key`, page.key)
    .addValue(PAGE_CONTROLLER, `version`, `${context.version}`)
    .addValue(PAGE_CONTROLLER, 'branch-id', `${context.branchId}`)
    .addValueIfExists(PAGE_CONTROLLER, 'qs-value', userDataParamString)
    .addTarget(FLOW_CONTROLLER, 'form')
    .add('data-starting-page-id', page.id)

  if (editingMode || liteEditingMode || allowJsonUpdate)
    pageAttrs
      .addController(JSON_UPDATE_CONTROLLER)
      .addValue(JSON_UPDATE_CONTROLLER, 'type', `page`)
      .addValue(JSON_UPDATE_CONTROLLER, 'path', `pages.${page.id}`)
      .addValue(JSON_UPDATE_CONTROLLER, 'page-id', page.id)
      .addValue(JSON_UPDATE_CONTROLLER, 'flow-id', context.flowId)
      .addValue(JSON_UPDATE_CONTROLLER, 'group-id', context.groupId)
      .addClass(JSON_UPDATE_CONTROLLER, 'loading', 'loading')

  //* I define the enter transition for the page
  const { transition: enterTransition, usePageClass: usePageEnterClass } = definePageTransition(
    page,
    flow,
    'enter'
  )

  if (usePageEnterClass) addPageTransitionAttr(page, flow, pageAttrs, 'enter', enterTransition)

  //* I define the exit transition for the page
  const { transition: exitTransition, usePageClass: usePageExitClass } = definePageTransition(
    page,
    flow,
    'exit'
  )

  if (usePageExitClass) addPageTransitionAttr(page, flow, pageAttrs, 'exit', exitTransition, true)

  const cssTag = `<style>${context.css}</style>`
  const elType = flow.form_wrapper ? 'form' : 'div'
  // @TODO: Make the ID unique even if there are multiple flows on the page
  const separateForm = !flow.form_wrapper ? `<form id="form-${context.flowId}"></form>` : ''
  const innerContent = `${separateForm}${beforePageGlobalComponents}
    <${elType} ${pageAttrs}>${pageHtml}</${elType}>
    ${afterPageGlobalComponents}`

  return context.popup
    ? generatePopupHTML(flowAttrs, flow, { innerContent, cssTag }, context.static_render)
    : `${cssTag}<div ${flowAttrs}>${innerContent}</div>`
}

function globalsAtLocation(
  page: FlowPage,
  location: GlobalComponentLocation,
  flow: Flow,
  groupId: string,
  editingMode: boolean,
  liteEditingMode: boolean,
  allowJsonUpdate: boolean,
  static_render: boolean,
  userData: { [key: string]: unknown },
  projectId: string
) {
  // const isExternalLocation = ['before_page', 'flow_bottom'].includes(location)
  const components = flow.components || []
  const containerKeys = new Set(
    components
      .filter((c) => c.type === 'Container')
      .map((c) => [c.id, c.key])
      .flat()
  )

  const builderVersion = getBaseBuilderVersion(flow.builder_version)
  const ancestryTree = buildComponentAncestryTree(components, builderVersion)
  const treeAtLocation = ancestryTree
    .filter((node) => node.component._location === location)
    .filter(({ component: { parent_key, parent_id } }) => {
      //* In builder v4, the parent_key is deprecated and only the parent_id is used
      const parentRef = builderVersion > 3 ? parent_id : parent_key || parent_id

      return parentRef ? !containerKeys.has(parentRef || '') : true
    })
  return ancestryTreeToHTML(
    treeAtLocation,
    page,
    flow,
    groupId,
    projectId,
    location,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    static_render,
    userData
  )
}

function generatePopupHTML(
  flowAttrs: Attrs,
  flow: Flow,
  { innerContent, cssTag }: { innerContent: string; cssTag: string },
  static_render: boolean
) {
  flowAttrs
    .addController(POPUP_CONTROLLER)
    .addDOMClass(`SavvyFlowPopup`, 'Flow-Element', 'ElementType-FlowPopupOuter')
    .addClass(POPUP_CONTROLLER, 'opened', 'popup-open')
    .addClass(POPUP_CONTROLLER, 'closed', 'popup-closed')
    .addValueIfExists(POPUP_CONTROLLER, 'selector', encodeURIComponent(flow.popup_selector!))
    .addAction(
      `${POPUP_CONTROLLER}:action->flow#dispatchAction`,
      `${POPUP_CONTROLLER}:prefill-update->flow#batchUpdate`,
      `${POPUP_CONTROLLER}:reset-page->flow#resetPage`
    )
  if (flow.popup_prevent_default)
    flowAttrs.addValue(POPUP_CONTROLLER, 'prevent', `${flow.popup_prevent_default}`)
  if (flow.reset_page_on_popup)
    flowAttrs.addValue(POPUP_CONTROLLER, 'reset-page', `${flow.reset_page_on_popup}`)

  const overlayAttrs = new Attrs(static_render)
    .addTarget(POPUP_CONTROLLER, 'overlay')
    .addDOMClass(
      'SavvyFlowPopup-overlay',
      'overlay',
      'Flow-Element',
      'ElementType-FlowPopupOverlay'
    )
    .addAction(`click->${POPUP_CONTROLLER}#closePopup`)

  const contentContainerAttrs = new Attrs(static_render)
    .addTarget(POPUP_CONTROLLER, 'contentContainer')
    .addDOMClass(
      'SavvyFlowPopup-container',
      'container',
      'Flow-Element',
      'ElementType-FlowPopupContainer'
    )
  const closeButtonAttrs = new Attrs(static_render)
    .addTarget(POPUP_CONTROLLER, 'closeButton')
    .addAction(`click->${POPUP_CONTROLLER}#closePopup`)
    .addDOMClass('close', 'borderless', 'Flow-Element', 'ElementType-FlowPopupCloseButton')

  const iconAttrs = new Attrs(static_render)
    .addIcon('feather', 'x')
    .addDOMClass('Flow-Element', 'ElementType-FlowPopupCloseButtonIcon')

  const boxContainerAttrs = new Attrs(static_render)
    .addTarget(POPUP_CONTROLLER, 'box')
    .addDOMClass('box', 'Flow-Element', 'ElementType-FlowPopupContainerBox')
  return `${cssTag}
      <div ${flowAttrs}>
        <div ${overlayAttrs}></div>
        <div ${contentContainerAttrs}>
          <button ${closeButtonAttrs}>
            <div ${iconAttrs}></div>
            Close
          </button>
          <div ${boxContainerAttrs}>${innerContent}</div>
        </div>
      </div>`
}

function renderSingleComponent(
  comp: Component,
  page: FlowPage,
  flow: Flow,
  groupId: string,
  projectId: string,
  globalLocation: string | null,
  editingMode: boolean | undefined,
  liteEditingMode: boolean | undefined,
  allowJsonUpdate: boolean | undefined,
  staticRender: boolean | undefined,
  userData: { [key: string]: unknown }
) {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)
  const componentChildrenTree = comp.components
    ? buildComponentAncestryTree([comp].concat(comp.components), builderVersion)
    : undefined

  return constructComponent(
    comp,
    page,
    flow,
    groupId,
    projectId,
    globalLocation,
    editingMode,
    liteEditingMode,
    allowJsonUpdate,
    staticRender,
    componentChildrenTree?.[0]?.children,
    userData
  )
}

function definePageTransition(page: FlowPage, flow: Flow, transitionType?: 'enter' | 'exit') {
  const useTransition = (val?: string) =>
    ![undefined, 'no-transition', 'no_transition'].includes(val)

  const transitionTypeJsonAttr = transitionType === 'exit' ? 'exit_transition' : 'enter_transition'

  //* I define the enter transition for the page
  let transition: Transition | { name: string | undefined } | undefined
  if (transitionType === 'enter' && page.transition?.['individual-content'])
    transition = { name: page.transition.content }
  else if (transitionType === 'enter' && flow.defaults?.transitions?.content)
    transition = { name: flow.defaults?.transitions?.content }
  else if (page.transition?.[transitionTypeJsonAttr])
    transition = page.transition[transitionTypeJsonAttr]
  else transition = flow.defaults?.transitions?.[transitionTypeJsonAttr]
  const usePageClass = useTransition(transition?.name)

  return { transition, usePageClass }
}

function addPageTransitionAttr(
  page: FlowPage,
  flow: Flow,
  pageAttrs: Attrs,
  transitionType: 'enter' | 'exit',
  transition?: Transition | { name: string | undefined },
  noTransitionClass?: boolean
) {
  if (!transition) return

  const transitionTypeJsonAttr = transitionType === 'exit' ? 'exit_transition' : 'enter_transition'
  const { name, duration, timing_function, delay } = transition as Transition

  let pageClass =
    page?.transition?.content || flow.defaults?.transitions?.content
      ? 'page-enter'
      : `page-${transitionType}-${name}-${duration || '300'}-${timing_function || 'linear'}`

  pageAttrs
    .addClass(PAGE_CONTROLLER, transitionType, pageClass)
    .addValue(
      PAGE_CONTROLLER,
      `${transitionType}-duration`,
      flow.defaults?.transitions?.[transitionTypeJsonAttr]?.duration?.toString() || '300'
    )

  if (delay) pageAttrs.addValue(PAGE_CONTROLLER, `exit-delay`, delay.toString() || '0')

  if (!noTransitionClass) pageAttrs.addDOMClass(pageClass)
}
